import type { ScreenContext } from '../../shared';
import { readAppearance, validateHeroName } from '../../shared/atelier';
import { applyPreset, buildDNA, dnaCodec, speciesFields } from '../dna';
import type { CreatureSave } from '../save';

/** Embedded and standalone saves have the same validated path into the active hero. */
export function saveAtelierHero(ctx: ScreenContext, data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const input = data as { appearance?: unknown; portrait?: unknown };
  const appearance = readAppearance(input.appearance);
  if (!appearance) return false;
  const name = validateHeroName(appearance.name);
  if (!name) return false;
  appearance.name = name;
  const species = appearance.model === 'fox-st' ? 'fox' : appearance.model === 'hare-st' ? 'hare' : 'cat';
  const presets = {
    none: { garment: 'none' }, crusader: { garment: 'tabard', headwear: 'kettle', cape: 'capelet' }, monk: { garment: 'robe', headwear: 'cowl' },
    king: { garment: 'robe', headwear: 'crown', cape: 'capelet' }, bishop: { garment: 'robe', headwear: 'mitre' }, wanderer: { garment: 'tunic', headwear: 'cowl' }, jester: { garment: 'tunic', headwear: 'jester' },
  };
  const fields = applyPreset(speciesFields(species), presets[appearance.outfit as keyof typeof presets]);
  const dna = dnaCodec.encode(buildDNA({ ...fields, nameMode: 1 }, name));
  const id = ctx.services.creatures.active()?.id ?? 'hero';
  const portrait = typeof input.portrait === 'string' && input.portrait.startsWith('data:image/webp;base64,') && input.portrait.length < 350000 ? input.portrait : undefined;
  ctx.store.update('creature', draft => {
    const save = draft as CreatureSave;
    save.roster ??= [];
    const row = save.roster.find(x => x.id === id);
    if (row) { row.dna = dna; row.appearance = appearance; row.portrait = portrait; }
    else save.roster.push({ id, dna, appearance, portrait, speciesChangedAt: null });
    save.activeId = id;
  }, 'creature:save-atelier');
  ctx.bus.emit('creature/changed', { creatureId: id });
  return true;
}
